import { memo } from 'react';
import { useRecoilState } from 'recoil';
import { LayoutDashboard, Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  LAYOUT_PRESETS,
  LayoutPreset,
  layoutPresetState
} from '@/state/layoutPreset';

/**
 * LayoutPresetPicker
 *
 * Popover selector for the workspace layout preset. Lets the user switch
 * between classic drag-drop and curated cockpit/focus/split layouts.
 * Each option renders a tiny schematic preview so the user can see the
 * shape of the chat / graph / terminal panels before picking.
 */
const PresetThumb = memo(({ preset }: { preset: LayoutPreset }) => {
  const base = 'absolute rounded-sm bg-primary/70';
  return (
    <div className="relative h-8 w-10 rounded border border-border bg-muted">
      {preset === 'classic' && (
        <>
          <span className={cn(base, 'left-0.5 top-0.5 bottom-0.5 w-[45%]')} />
          <span className={cn(base, 'right-0.5 top-0.5 h-[45%] w-[45%]')} />
          <span className={cn(base, 'right-0.5 bottom-0.5 h-[45%] w-[45%]')} />
        </>
      )}
      {preset === 'cockpit' && (
        <>
          <span className={cn(base, 'left-0.5 top-0.5 bottom-0.5 w-[55%]')} />
          <span className={cn(base, 'right-0.5 top-0.5 h-[45%] w-[35%]')} />
          <span className={cn(base, 'right-0.5 bottom-0.5 h-[45%] w-[35%]')} />
        </>
      )}
      {preset === 'focus' && (
        <>
          <span className={cn(base, 'left-0.5 top-0.5 bottom-0.5 w-[70%]')} />
          <span className={cn(base, 'right-0.5 top-0.5 bottom-0.5 w-[20%] opacity-60')} />
        </>
      )}
      {preset === 'split' && (
        <>
          <span className={cn(base, 'left-0.5 right-0.5 top-0.5 h-[45%]')} />
          <span className={cn(base, 'left-0.5 bottom-0.5 h-[45%] w-[45%]')} />
          <span className={cn(base, 'right-0.5 bottom-0.5 h-[45%] w-[45%]')} />
        </>
      )}
    </div>
  );
});
PresetThumb.displayName = 'PresetThumb';

const LayoutPresetPicker = memo(() => {
  const [preset, setPreset] = useRecoilState(layoutPresetState);
  const current = LAYOUT_PRESETS.find((p) => p.id === preset) ?? LAYOUT_PRESETS[0];

  return (
    <TooltipProvider delayDuration={150}>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 rounded-full border-border px-2.5 text-muted-foreground hover:text-foreground"
                aria-label={`Layout: ${current.label}`}
              >
                <LayoutDashboard className="h-3.5 w-3.5" />
                <span className="hidden md:inline text-xs font-medium">{current.label}</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Workspace layout</TooltipContent>
        </Tooltip>
        <PopoverContent
          align="end"
          sideOffset={6}
          className="w-72 p-1 bg-popover text-popover-foreground border-border shadow-lg"
        >
          <div className="flex flex-col">
            {LAYOUT_PRESETS.map((option) => {
              const active = option.id === preset;
              return (
                <button
                  type="button"
                  key={option.id}
                  onClick={() => setPreset(option.id)}
                  className={cn(
                    'flex items-start gap-3 rounded-md px-2.5 py-2 text-left transition-colors',
                    active
                      ? 'bg-primary/10 text-foreground'
                      : 'hover:bg-accent hover:text-foreground'
                  )}
                >
                  <PresetThumb preset={option.id} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">{option.label}</span>
                      {active && <Check className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {option.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
});

LayoutPresetPicker.displayName = 'LayoutPresetPicker';

export default LayoutPresetPicker;
